import {
    Grid,
    CardHeader,
    CardContent,
    Box,
    Typography,
    Divider,
    Card,
    Chip,
    Stack,
    CircularProgress,
    Alert,
    Button,
    Avatar,
  } from "@mui/material";
  import React, { useEffect, useState } from "react";
  import { useParams, useNavigate } from "react-router-dom";
  import { Helmet } from "react-helmet-async";
  import { OrderServices } from "../../services/AllServices";
  import {
    ShoppingCart as ShoppingCartIcon,
    Person as PersonIcon,
    DirectionsCar as DirectionsCarIcon,
    AttachMoney as AttachMoneyIcon,
    CalendarToday as CalendarTodayIcon,
    Gavel as GavelIcon,
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    LocalShipping as LocalShippingIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Palette as PaletteIcon,
    Speed as SpeedIcon,
  } from "@mui/icons-material";
  
  function Detail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
  
    const fetchDetails = async () => {
      try {
        const response = await OrderServices.getOrderDetails({ orderId: id });
        setData(response?.data?.data || null);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
  
    useEffect(() => {
      fetchDetails();
    }, [id]);
  
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount || 0);
    };
  
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };
  
    const getStatusChip = (status) => {
      const statusConfig = {
        completed: { label: "Completed", color: "success", icon: <CheckCircleIcon /> },
        pending: { label: "Pending", color: "warning", icon: <LocalShippingIcon /> },
        processing: { label: "Processing", color: "info", icon: <LocalShippingIcon /> },
        cancelled: { label: "Cancelled", color: "error", icon: <LocalShippingIcon /> },
      };
  
      const config = statusConfig[status?.toLowerCase()] || { 
        label: status || "Unknown", 
        color: "default", 
        icon: <LocalShippingIcon /> 
      };
  
      return (
        <Chip 
          label={config.label} 
          color={config.color} 
          size="small" 
          icon={config.icon}
        />
      );
    };
  
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }
  
    if (!data) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="info">Order not found</Alert>
        </Box>
      );
    }
  
    return (
      <Box>
        <Helmet>
          <title>Order Details - {data.orderId}</title>
        </Helmet>
  
        {/* Page Header Section */}
        <Box className="addedit-user-container">
          <Box className="page-header-section">
            <Box className="page-header">
              <ShoppingCartIcon color="primary" />
              <Typography variant="h4" component="h1" className="page-title">
                Order Details
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" className="page-subtitle">
              View comprehensive order information, buyer details, and payment summary
            </Typography>
          </Box>
  
          {/* Action Buttons Section */}
          <Box className="section-wrapper">
            <Card elevation={1} className="section-card">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/ordermanagement")}
                    className="submit-button"
                  >
                    Back to Orders
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <br/>
  
        
          <Box className="section-wrapper mt-2">
            <Card elevation={1} className="section-card">
              <CardHeader
                title={
                  <Box className="section-header">
                    <PersonIcon color="primary" />
                    <Typography variant="h5" className="section-title">
                      Buyer Information
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
                  <Avatar sx={{ width: 64, height: 64, bgcolor: "primary.main", fontSize: "1.5rem" }}>
                    {data.buyer?.name?.charAt(0) || "?"}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {data.buyer?.name || "N/A"}
                    </Typography>
                   
                  </Box>
                </Box>
  
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      p: 2.5, 
                      bgcolor: "grey.50", 
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "grey.100",
                        transform: "translateX(4px)"
                      }
                    }}>
                      <EmailIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                          Email
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {data.buyer?.email || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ 
                      p: 2.5, 
                      bgcolor: "grey.50", 
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "grey.100",
                        transform: "translateX(4px)"
                      }
                    }}>
                      <PhoneIcon color="action" />
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                          Phone
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {data.buyer?.contactNumber || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
  
          {/* Vehicle Information Section */}
          <Box className="section-wrapper mt-2">
            <Card elevation={1} className="section-card">
              <CardHeader
                title={
                  <Box className="section-header">
                    <DirectionsCarIcon color="primary" />
                    <Typography variant="h5" className="section-title">
                      Vehicle Details
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {data.vehicle?.vehicleName || "N/A"}
                  </Typography>
                </Box>
  
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard label="Make" value={data.vehicle?.make} icon={<DirectionsCarIcon />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard label="Model" value={data.vehicle?.model} icon={<DirectionsCarIcon />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard label="Year" value={data.vehicle?.year} icon={<CalendarTodayIcon />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard label="Color" value={data.vehicle?.color} icon={<PaletteIcon />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard label="Interior Color" value={data.vehicle?.interiorColor} icon={<PaletteIcon />} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <InfoCard label="Exterior Color" value={data.vehicle?.exteriorColor} icon={<PaletteIcon />} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
  
          {/* Payment Details Section */}
          <Box className="section-wrapper mt-2">
            <Card elevation={1} className="section-card">
              <CardHeader
                title={
                  <Box className="section-header">
                    <AttachMoneyIcon color="primary" />
                    <Typography variant="h5" className="section-title">
                      Payment Details
                    </Typography>
                  </Box>
                }
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      textAlign: "center", 
                      p: 3, 
                      bgcolor: "grey.50", 
                      borderRadius: 3,
                      border: "1px solid #E5E7EB",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "grey.100",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                      }
                    }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        Final Price
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {formatCurrency(data.finalPrice)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      textAlign: "center", 
                      p: 3, 
                      bgcolor: "warning.50", 
                      borderRadius: 3,
                      border: "1px solid #F59E0B",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "warning.100",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(245, 158, 11, 0.2)"
                      }
                    }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        Admin Fees
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="warning.main">
                        {formatCurrency(data.adminFees)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      textAlign: "center", 
                      p: 3, 
                      bgcolor: "success.50", 
                      borderRadius: 3,
                      border: "1px solid #10B981",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "success.100",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(16, 185, 129, 0.2)"
                      }
                    }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                        Total Paid
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="success.main">
                        {formatCurrency(data.totalAmountPaid)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    );
  }
  
  // Reusable Info Card Component
  function InfoCard({ label, value, icon }) {
    return (
      <Box sx={{ 
        p: 2.5, 
        bgcolor: "grey.50", 
        borderRadius: 3,
        border: "1px solid #E5E7EB",
        transition: "all 0.2s ease",
        "&:hover": {
          bgcolor: "grey.100",
          transform: "translateY(-2px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
        }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {icon && React.cloneElement(icon, { fontSize: "small", color: "action" })}
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>
            {label}
          </Typography>
        </Box>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: "1.1rem" }}>
          {value || "—"}
        </Typography>
      </Box>
    );
  }
  
  export default Detail;